// src/contexts/AuthContext.jsx
// Firebase Auth + Firestore user document bootstrap.
// CLAUDE.md "4. 데이터 모델" 의 users 스키마와 동기화 유지.

import { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  createUserWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  runTransaction,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { nanoid } from 'nanoid';

import {
  auth, db,
  googleProvider, appleProvider, facebookProvider, kakaoProvider, lineProvider,
} from '../firebase.js';
import { ga } from '../utils/ga4.js';
import { SIGNUP_CLOSED } from '../utils/siteMode.js';
import { detectInAppBrowser } from '../utils/inAppBrowser.js';

const AuthContext = createContext(null);

const REFERRAL_MONTHLY_CAP = 10;

// 소셜 제공자 레지스트리 — utils/authProviders.js 의 키와 동일하게 유지.
const SOCIAL_PROVIDERS = {
  google: googleProvider,
  apple: appleProvider,
  facebook: facebookProvider,
  kakao: kakaoProvider,
  line: lineProvider,
};

// 리다이렉트 전에 가입 의도(국가/추천코드)를 보관 → 복귀 후 getRedirectResult 에서 사용.
const INTENT_KEY = 'cc.authIntent';

// 신규 가입 종료 스위치 (2026-08-28) — src/utils/siteMode.js 참조.
function signupClosedError() {
  const e = new Error('SIGNUP_CLOSED');
  e.code = 'auth/signup-closed';
  return e;
}

async function ensureUserDoc(uid, baseData) {
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  if (snap.exists()) return snap.data();

  // ★ 신규 가입 종료 (2026-08-28). 이메일/구글/카카오/라인/리다이렉트 복귀 —
  //   계정을 만드는 경로는 전부 이 함수를 지나므로 여기가 유일한 길목이다.
  //   Auth 세션만 남고 users 문서가 없으면 앱이 반쯤 로그인된 이상 상태로 보이므로 로그아웃까지 시킨다.
  //   (실효적 차단은 firestore.rules 의 users create 허용목록이다 — 클라이언트는 우회 가능.)
  if (SIGNUP_CLOSED) {
    try { await signOut(auth); } catch { /* 이미 로그아웃 */ }
    throw signupClosedError();
  }

  const referralCode = nanoid(8);
  const data = {
    email: baseData.email ?? null,
    country: baseData.country ?? 'KR',
    plan: 'free',
    credits: 3,        // 가입 보너스 = 초기 잔액
    bonusCredits: 3,   // 보호되는 보너스 풀 — 구독 취소 시에도 보존, 사용량 만큼만 감소
    creditsResetAt: null,
    referralCode,
    referredBy: baseData.referredBy ?? null,
    referralRewarded: false,  // 추천 보너스 미지급 — 서버 측 process-referral 이 true 로 갱신
    polarCustomerId: null,
    channelLogoUrl: null,
    createdAt: serverTimestamp(),
  };

  await setDoc(ref, data);

  // 역색인용 referrals/{code} 도큐먼트도 생성 (서버사이드만 갱신 가능하지만
  // 가입 직후 본인이 한 번 만들 수 있도록 클라이언트에서 시도. 실패해도 무시)
  try {
    await setDoc(doc(db, 'referrals', referralCode), { ownerUid: uid, used: 0 });
  } catch {
    /* rules로 막혀 있으면 무시 — 서버사이드에서 갱신 */
  }

  return data;
}

// 서버사이드 process-referral 호출 — 추천인 + 가입자 모두 ⚡1 / 보너스 ⚡1, 월 상한 10.
// 클라이언트 측 직접 갱신은 Firestore rules 로 막혀있으므로 항상 서버 경유.
async function applyReferralBonus(uid) {
  try {
    const res = await fetch('/api/process-referral', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid }),
    });
    const json = await res.json().catch(() => null);
    console.log('[referral] process-referral result:', json);
    return json;
  } catch (e) {
    console.warn('[referral] failed:', e);
    return null;
  }
}

// 소셜 인증 성공 후 공통 처리: 첫 로그인이면 user 도큐먼트 생성(가입) + 추천코드.
// 팝업(즉시 결과) / 리다이렉트(getRedirectResult) 양쪽에서 재사용.
async function provisionAfterAuth(fbUser, intent = {}, providerId = 'social') {
  const ref = doc(db, 'users', fbUser.uid);
  const before = await getDoc(ref);
  if (!before.exists()) {
    await ensureUserDoc(fbUser.uid, {
      email: fbUser.email,
      country: intent.country || 'KR',
      referredBy: intent.referralCode || null,
    });
    ga.signUp((providerId || 'social').replace('.com', ''));
    if (intent.referralCode) await applyReferralBonus(fbUser.uid);
  } else if (intent.referralCode && !before.data().referredBy) {
    try { await updateDoc(ref, { referredBy: intent.referralCode }); } catch { /* rules */ }
    if (!before.data().referralRewarded) await applyReferralBonus(fbUser.uid);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // profile snapshot 이 한 번이라도 응답했는지. 라우트가드가 "아직 모름" vs "doc 없음" 구분 위해 필요.
  const [profileLoaded, setProfileLoaded] = useState(false);
  // 리다이렉트 소셜 로그인 복귀 처리 중/에러 — 페이지에서 스피너·토스트로 사용.
  const [socialBusy, setSocialBusy] = useState(false);
  const [socialError, setSocialError] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      setUser(fbUser);
      setLoading(false);
    });
    return unsub;
  }, []);

  // 소셜 리다이렉트 복귀 처리: 첫 로그인이면 user 도큐먼트 자동 생성(가입), 추천코드 적용.
  // 로그인/가입을 하나로 통합 — 카톡 등에서 공유링크로 들어온 신규 사용자가 매끄럽게 시작.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let result;
      try {
        result = await getRedirectResult(auth);
      } catch (e) {
        console.error('[auth] redirect 결과 오류:', e?.code, e?.message);
        if (!cancelled) setSocialError(e?.code || e?.message || 'auth/redirect-failed');
        return;
      }
      if (!result?.user || cancelled) return;
      setSocialBusy(true);
      try {
        let intent = {};
        try { intent = JSON.parse(sessionStorage.getItem(INTENT_KEY) || '{}'); } catch { /* noop */ }
        sessionStorage.removeItem(INTENT_KEY);
        await provisionAfterAuth(result.user, intent, result.providerId);
      } finally {
        if (!cancelled) setSocialBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // user → users/{uid} 도큐먼트 실시간 구독
  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileLoaded(true);
      return undefined;
    }
    setProfileLoaded(false);
    const ref = doc(db, 'users', user.uid);
    // includeMetadataChanges: cache vs server 응답 구분.
    // 새로고침 직후 첫 응답이 cache 에서 "doc 없음" 으로 오는 race 가 있어,
    // 그 경우 server 응답을 기다린다 (profileLoaded 를 그때까지 false 유지).
    const unsub = onSnapshot(
      ref,
      { includeMetadataChanges: true },
      (snap) => {
        // doc 없음 + cache 응답이면 무시 — server 응답 기다림
        if (!snap.exists() && snap.metadata.fromCache) return;
        setProfile(snap.exists() ? { uid: user.uid, ...snap.data() } : null);
        setProfileLoaded(true);
      },
      () => {
        setProfile(null);
        setProfileLoaded(true);
      },
    );
    return unsub;
  }, [user]);

  // 통합 소셜 인증 (로그인=가입).
  // - 일반 브라우저: 팝업. 앱 도메인(citizen-cast.com)과 authDomain(*.firebaseapp.com)이 달라
  //   signInWithRedirect 는 교차 도메인 저장 차단으로 복귀 시 세션 유실됨 → 팝업으로 회피.
  // - 인앱 브라우저(웹뷰): 팝업이 막히므로 리다이렉트 (카카오/라인은 자기 앱 웹뷰에서 작동).
  async function socialAuth(providerName, { country = 'KR', referralCode = null } = {}) {
    const provider = SOCIAL_PROVIDERS[providerName];
    if (!provider) throw new Error(`unknown provider: ${providerName}`);
    setSocialError(null);
    const intent = { country, referralCode };

    if (detectInAppBrowser()) {
      try {
        sessionStorage.setItem(INTENT_KEY, JSON.stringify({ ...intent, ts: Date.now() }));
      } catch { /* private mode 등 — 무시 */ }
      await signInWithRedirect(auth, provider);
      return;
    }

    setSocialBusy(true);
    try {
      const result = await signInWithPopup(auth, provider);
      await provisionAfterAuth(result.user, intent, result.providerId);
    } finally {
      setSocialBusy(false);
    }
  }

  // 부분 가입 복구: 이미 로그인(Auth) 됐는데 user 도큐먼트가 없는 경우 재인증 없이 doc 생성.
  // 리다이렉트가 아닌 일반 진입(세션 유지)에서 ProtectedRoute 가 /register 로 보낸 케이스용.
  async function completeSignup(country = 'KR', referralCode = null) {
    const fbUser = auth.currentUser;
    if (!fbUser) { const e = new Error('NO_AUTH'); e.code = 'auth/no-current-user'; throw e; }
    const ref = doc(db, 'users', fbUser.uid);
    const before = await getDoc(ref);
    if (!before.exists()) {
      await ensureUserDoc(fbUser.uid, { email: fbUser.email, country, referredBy: referralCode });
      if (referralCode) await applyReferralBonus(fbUser.uid);
    } else if (referralCode && !before.data().referredBy) {
      try { await updateDoc(ref, { referredBy: referralCode }); } catch { /* rules */ }
      if (!before.data().referralRewarded) await applyReferralBonus(fbUser.uid);
    }
    return fbUser;
  }

  async function login(email, password) {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return cred.user;
  }

  // 로그인 — 가입된 계정만 통과. doc 없으면 즉시 signOut + 에러.
  async function loginWithGoogle() {
    let fbUser = auth.currentUser;
    if (!fbUser) {
      const cred = await signInWithPopup(auth, googleProvider);
      fbUser = cred.user;
    }
    const before = await getDoc(doc(db, 'users', fbUser.uid));
    if (!before.exists()) {
      await signOut(auth);
      const err = new Error('NOT_REGISTERED');
      err.code = 'auth/not-registered';
      throw err;
    }
    return fbUser;
  }

  // 회원가입 — 신규면 doc 생성 + 추천 보너스. 이미 있으면 누락 필드 보수 + 추천 retry.
  async function registerWithGoogle(country = 'KR', referralCode = null) {
    let fbUser = auth.currentUser;
    if (!fbUser) {
      const cred = await signInWithPopup(auth, googleProvider);
      fbUser = cred.user;
    }
    const ref = doc(db, 'users', fbUser.uid);
    const before = await getDoc(ref);
    if (!before.exists()) {
      // 신규 가입
      await ensureUserDoc(fbUser.uid, {
        email: fbUser.email,
        country,
        referredBy: referralCode,
      });
      if (referralCode) await applyReferralBonus(fbUser.uid);
    } else {
      // 부분 가입 / 보수 케이스 — referredBy 가 비어있고 사용자가 추천코드 입력했으면 기록 시도
      const cur = before.data();
      const patch = {};
      if (referralCode && !cur.referredBy) patch.referredBy = referralCode;
      // (credits / referralRewarded / bonusCredits 같은 보호 필드는 Firestore rules 로 막혀
      //  클라이언트에서 못 건드림. 서버사이드 process-referral 이 처리.)
      if (Object.keys(patch).length > 0) {
        try { await updateDoc(ref, patch); } catch { /* rules 로 거부될 수 있음 */ }
      }
      // 추천 보상 미지급이고 referredBy 가 있으면 재시도
      const hasRef = referralCode || cur.referredBy;
      if (hasRef && !cur.referralRewarded) {
        await applyReferralBonus(fbUser.uid);
      }
    }
    return fbUser;
  }

  async function signup(email, password, country, referralCode) {
    // ensureUserDoc 보다 먼저 막는다 — createUserWithEmailAndPassword 가 통과하면
    // users 문서 없는 '고아 Auth 계정' 이 남는다.
    if (SIGNUP_CLOSED) throw signupClosedError();
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await ensureUserDoc(cred.user.uid, {
      email: cred.user.email,
      country,
      referredBy: referralCode || null,
    });
    if (referralCode) await applyReferralBonus(cred.user.uid);
    return cred.user;
  }

  async function logout() {
    await signOut(auth);
  }

  const value = {
    user,
    profile,
    loading,
    profileLoaded,
    socialBusy,
    socialError,
    socialAuth,
    completeSignup,
    login,
    loginWithGoogle,
    registerWithGoogle,
    signup,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
